import { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { useGetBankPaymentParsersQuery } from '@widgets/AddPayments/lib/hooks/useGetBankPaymentParsersQuery';
import { useImportCompanyPaymentsQuery } from '@widgets/AddPayments/lib/hooks/useImportCompanyPaymentsQuery';
import { updateFilter } from '@widgets/FilterPayments/ui/updateFilter';

import {
    fetchDataForEdit,
    fetchPaymentsFilters,
} from '../../../../store/slices/paymentSlice/action';
import { paymentActions } from '../../../../store/slices/paymentSlice/paymentSlice';

function useDebounce(value, delay) {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
        const timer = setTimeout(() => setDebouncedValue(value), delay || 500);

        return () => {
            clearTimeout(timer);
        };
    }, [value, delay]);

    return debouncedValue;
}

export const useFilterPayments = () => {
    const { t } = useTranslation();

    const dispatch = useDispatch();
    const {
        isLoadingFilters: isLoadingFiltersPayments,
        accountsSelectList: allAccounts,
        filter,
        archiveAccountList: allAccountsArchive,
        partnersSelectList: allPartner,
        expenseList: allExpense,
        incomeList: allIncome,
        estimatesSelectList: allEstimates,
        isLoadingDataForEdit,
    } = useSelector((state) => state.paymentReducer);
    const [paymentStartDate, setPaymentStartDate] = useState(null);
    const [paymentEndDate, setPaymentEndDate] = useState(null);
    const [accrualStartDate, setAccrualStartDate] = useState(null);
    const [accrualEndDate, setAccrualEndDate] = useState(null);

    const projects = allEstimates
        .filter((es) => es.isHead)
        .map((es) => ({ value: es.value, title: es.title }));

    const statusList = [
        {
            title: t('FilterPayments.statusList.all'),
            value: 'all',
        },
        {
            title: t('FilterPayments.statusList.success'),
            value: 'success',
        },
        {
            title: t('FilterPayments.statusList.unsettled'),
            value: 'unsettled',
        },
        {
            title: t('FilterPayments.statusList.sent'),
            value: 'sent',
        },
        {
            title: t('FilterPayments.statusList.processing'),
            value: 'processing',
        },
        {
            title: t('FilterPayments.statusList.cancelled'),
            value: 'cancelled',
        },
    ];

    const projectOptions = [
        {
            title: t('FilterPayments.projectOptions.all'),
            value: 'all',
        },
        ...projects,
    ];

    const partnerOptions = [
        {
            title: t('FilterPayments.partnerOptions.all'),
            value: 'all',
        },
        ...allPartner,
    ];

    const expenditureOptions = [
        {
            title: t('FilterPayments.expenditureOptions.all'),
            value: 'all',
        },
        {
            title: t('FilterPayments.expenditureOptions.income'),
            value: 'income',
            isHead: true,
            isDisabled: true,
            children: allIncome,
        },
        ...allIncome,
        {
            title: t('FilterPayments.expenditureOptions.expense'),
            value: 'expense',
            isHead: true,
            isDisabled: true,
            children: allExpense,
        },
        ...allExpense,
    ];

    useEffect(() => {
        dispatch(fetchDataForEdit());
    }, []);

    const [accounts, setAccounts] = useState([]);
    const [project, setProject] = useState('all');
    const [paymentItems, setPaymentItems] = useState('all');
    const [partners, setPartners] = useState('all');
    const [status, setStatus] = useState('all');

    const [inputsState, setInputsState] = useState({
        sumStart: '',
        sumEnd: '',
        findInput: '',
    });
    const debouncedValue = useDebounce(inputsState.sumStart, 500);
    const debouncedValueEnd = useDebounce(inputsState.sumEnd, 500);

    const accountHandler = (value) => {
        const isNewAccount = accounts.some((i) => i.value === value);

        if (isNewAccount === false) {
            const currentAccount = [...allAccounts, ...allAccountsArchive].find(
                (i) => i.value === value
            );
            setAccounts([...accounts, currentAccount]);
        } else {
            const newList = accounts.filter((i) => i.value !== value);
            setAccounts(newList);
        }
    };

    const projectHandler = (value) => {
        setProject(value);
    };
    const statusHandler = (value) => {
        setStatus(value);
    };
    const partnerHandler = (value) => {
        setPartners(value);
    };
    const expenditureHandler = (value) => {
        setPaymentItems(value);
    };
    const deleteHandle = (value) => {
        const newList = accounts.filter((i) => i.value !== value);
        setAccounts(newList);
    };

    const selectedPartner = partnerOptions.find((partnerItem) => partnerItem.value === partners);
    const selectedProject = projectOptions.find((projectItem) => projectItem.value === project);
    const selectedExpenditure = expenditureOptions.find((ex) => ex.value === paymentItems);
    const statusExpenditure = statusList.find((ex) => ex.value === status);

    const inputHandler = (event, mode = '') => {
        const { name, value } = event.target;
        const newValue = { ...inputsState, [name]: value };

        setInputsState((prevState) => newValue);
    };
    const inputFindHandler = (event, mode = '') => {
        const { name, value } = event.target;
        setInputsState((prevState) => ({ ...prevState, [name]: value }));
    };

    const buttonHandler = () => {
        updateFilter(
            dispatch,
            paymentActions,
            fetchPaymentsFilters,
            accounts,
            inputsState,
            partners,
            paymentItems,
            project,
            paymentStartDate,
            paymentEndDate,
            accrualStartDate,
            accrualEndDate,
            filter,
            status
        );
    };

    useEffect(() => {
        updateFilter(
            dispatch,
            paymentActions,
            fetchPaymentsFilters,
            accounts,
            inputsState,
            partners,
            paymentItems,
            project,
            paymentStartDate,
            paymentEndDate,
            accrualStartDate,
            accrualEndDate,
            filter,
            status
        );
    }, [
        accounts,
        project,
        paymentItems,
        partners,
        paymentStartDate,
        paymentEndDate,
        accrualStartDate,
        accrualEndDate,
        status,
        debouncedValue,
        debouncedValueEnd,
    ]);

    const onPaymentDateChange = (newDate) => {
        setPaymentEndDate(newDate.end);
        setPaymentStartDate(newDate.start);
    };

    const onAccrualDateChange = (newDate) => {
        setAccrualEndDate(newDate.end);
        setAccrualStartDate(newDate.start);
    };
    const onPaymentDateChangeCancel = () => {
        setPaymentEndDate(null);
        setPaymentStartDate(null);
    };

    const onAccrualDateChangeCancel = () => {
        setAccrualEndDate(null);
        setAccrualStartDate(null);
    };

    const { isFetching, isLoading } = useGetBankPaymentParsersQuery();

    const isLoadingFilters =
        isFetching || isLoading || isLoadingFiltersPayments || isLoadingDataForEdit;

    return {
        accountHandler,
        projectHandler,
        statusHandler,
        partnerHandler,
        expenditureHandler,
        deleteHandle,
        selectedPartner,
        selectedProject,
        selectedExpenditure,
        statusExpenditure,
        inputHandler,
        inputFindHandler,
        buttonHandler,
        onPaymentDateChange,
        onAccrualDateChange,
        onPaymentDateChangeCancel,
        onAccrualDateChangeCancel,
        t,
        accounts,
        allAccounts,
        allAccountsArchive,
        projectOptions,
        partnerOptions,
        expenditureOptions,
        statusList,
        inputsState,
        paymentStartDate,
        paymentEndDate,
        accrualStartDate,
        accrualEndDate,
        isLoadingFilters,
    };
};
